import React, { useRef, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Skeleton,
  Divider,
  Popover,
  MenuItem,
  ListItemText,
  List,
  ListItem,
  IconButton,
} from "@mui/material";
import Typography from "../../components/Typography/Typography";
import Button from "../../components/Button/Button";
import EditableTitle from "./EditableTitle/EditableTitle";
import GeneratedSvg from "../../assets/images/generated_content_icon.svg";
import ShareSvg from "../../assets/images/share_icon.svg";
import SummarySvg from "../../assets/images/summary_icon.svg";
import ThreeDotsSvg from "../../assets/images/threedots_icon.svg";
import ExpandSvg from "../../assets/images/expand_icon.svg";
import HiddenEyeSvg from "../../assets/images/hidden_eye_icon.svg";
import GeminiToggleSvg from "../../assets/images/gemini_toggle_icon.svg";
import OpenEyeSvg from "../../assets/images/open_eye_icon.svg";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DashboardStyles from "./Dashboard.module.scss";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  exportArticle,
  updateArticleStatus,
} from "../../services/common.service";
import GreyCrossSvg from "../../assets/images/grey_cross.svg";
import { useAppSelector } from "../../hooks/store";
import { getToast } from "../../common/utils";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../common/constants";
import rehypeRaw from "rehype-raw";
import Tooltip from "../../components/Tooltip/Tooltip";

function replaceCitationsWithTags(markdown: string): string {
  return markdown.replace(/\[(\d+(?:,\d+)*)\]/g, (_match, p1) => {
    return `<CITE data-cite="${p1}"/>`;
  });
}

interface CitationTooltipProps {
  numbers: string;
  citationsDict: { [number: string]: string };
  onClick?: () => void;
}

const CitationTooltip: React.FC<CitationTooltipProps> = ({
  numbers,
  citationsDict,
  onClick,
}) => {
  const citationNames = numbers
    .split(",")
    .map((num) => citationsDict[`[${num}]`] || `[${num}]`)
    .join(", ");
  return (
    <Tooltip title={citationNames} arrow>
      <span
        style={{
          color: "#3474E0",
          cursor: "pointer",
          textDecoration: "underline dotted",
        }}
        onClick={onClick}
      >
        [{numbers}]
      </span>
    </Tooltip>
  );
};

export interface GeneratedContentData {
  title: string;
  content: string;
  sources: string[];
  article_with_citations: string;
  citations: { [name: string]: string };
  citations_dict: { [number: string]: string };
  sources_status?: { [sourceName: string]: boolean };
}

interface GeneratedContentLoaderProps {
  showGeminiIcon?: boolean;
  geminiText?: string;
}

interface GeneratedContentProps {
  content: GeneratedContentData | null;
  isLoading?: boolean;
  onContentUpdate?: (updatedContent: GeneratedContentData) => void;
}

// Loading/Shimmer Component for Generated Content
const GeneratedContentLoader: React.FC<GeneratedContentLoaderProps> = ({
  showGeminiIcon = false,
  geminiText = "Generating article draft. This may take a few moments.",
}) => {
  return (
    <Card
      sx={{
        height: "100%",
        background: "#FFFFFF99",
        borderRadius: "16px",
        boxShadow: "none",
        animation: "fadeIn 0.3s ease-in-out",
        "@keyframes fadeIn": {
          from: { opacity: 0, transform: "translateY(10px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <CardContent>
        <div className={DashboardStyles.generatedContentWrapper}>
          {showGeminiIcon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 2,
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%": { opacity: 0.6 },
                  "50%": { opacity: 1 },
                  "100%": { opacity: 0.6 },
                },
              }}
            >
              <img
                src={GeminiToggleSvg}
                alt=""
                style={{ width: "16px", height: "16px" }}
              />
              <Typography fontSize={12} color="#666">
                {geminiText}
              </Typography>
            </Box>
          )}

          <div className={DashboardStyles.generatedContainerHeader}>
            <div className={DashboardStyles.generatedTextIcon}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={120} height={20} />
            </div>
            <div className={DashboardStyles.generatedHeaderIcons}>
              {[...Array(4)].map((_, index) => (
                <Skeleton
                  key={index}
                  variant="circular"
                  width={24}
                  height={24}
                />
              ))}
            </div>
          </div>

          <div className={DashboardStyles.generatedContent}>
            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" width="100%" height={80} />
          </div>

          <div className={DashboardStyles.generatedContentFooter}>
            <div className={DashboardStyles.sourcesCitations}>
              <Skeleton variant="rounded" width={80} height={24} />
              <div className={DashboardStyles.citations}>
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton variant="text" width={60} height={16} />
              </div>
            </div>
            <div className={DashboardStyles.buttons}>
              <Skeleton variant="rounded" width={80} height={32} />
              <Skeleton variant="rounded" width={80} height={32} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main GeneratedContent Component
const GeneratedContent: React.FC<GeneratedContentProps> = ({
  content,
  isLoading = false,
  onContentUpdate,
}) => {
  const navigate = useNavigate();
  const [showCitations, setShowCitations] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [sourcesAnchorEl, setSourcesAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [isExporting, setIsExporting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const citationsRef = useRef<HTMLDivElement | null>(null);

  const session_id = useAppSelector(
    (store) => store?.rootReducer?.commonReducer?.session_id
  );

  const handleThreeDotsClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleSourcesClick = (event: React.MouseEvent<HTMLElement>) => {
    setSourcesAnchorEl(event.currentTarget);
  };

  const handleSourcesPopoverClose = () => {
    setSourcesAnchorEl(null);
  };

  const handleTitleUpdate = (newTitle: string) => {
    if (content && onContentUpdate) {
      const updatedContent = {
        ...content,
        title: newTitle,
      };
      onContentUpdate(updatedContent);
    }
  };

  const handleExportToWord = async () => {
    if (!content) return;

    setIsExporting(true);
    handlePopoverClose();

    try {
      const citations_value = showCitations;
      const response = await exportArticle(session_id, {
        output_format: "docx",
        need_citations: citations_value,
      });

      // Create a blob from the binary response
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      // Create a download URL
      const downloadUrl = window.URL.createObjectURL(blob);

      // Create a temporary anchor element and trigger download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${content.title
        .replace(/[^a-z0-9]/gi, "_")
        .toLowerCase()}.docx`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error exporting article:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const open = Boolean(anchorEl);
  const sourcesOpen = Boolean(sourcesAnchorEl);

  if (!content) return null;

  // Choose which content to show
  const markdownToShow =
    showCitations && content.article_with_citations
      ? content.article_with_citations
      : content.content;

  // Get sources data for popover
  const sourcesData = content.sources_status || {};
  const totalSources = Object.keys(sourcesData).length;
  const activeSources = Object.values(sourcesData).filter(
    (value) => value === true
  ).length;

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const reqBody = { status: "draft" };
      const response = await updateArticleStatus(session_id, reqBody);
      getToast("Success", "Draft Saved Successfully!");
      setSavingDraft(false);
    } catch (err) {
      getToast("Error", err);
      setSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const reqBody = { status: "published" };
      const response = await updateArticleStatus(session_id, reqBody);
      getToast("Success", "Article Published Successfully!");
      setPublishing(false);
      navigate(ROUTES.dashboard);
    } catch (err) {
      getToast("Error", err);
      setPublishing(false);
    }
  };

  return (
    <Card
      sx={{
        height: "100%",
        background: "#FFFFFF99",
        borderRadius: "16px",
        boxShadow: "none",
        animation: "slideIn 0.5s ease-out",
        "@keyframes slideIn": {
          from: { opacity: 0, transform: "translateX(20px)" },
          to: { opacity: 1, transform: "translateX(0)" },
        },
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ "&:last-child": { paddingBottom: 0 } }}>
        <div className={DashboardStyles.generatedContentWrapper}>
          <div className={DashboardStyles.generatedContainerHeader}>
            <div className={DashboardStyles.generatedTextIcon}>
              <img src={GeneratedSvg} alt="" />
              <EditableTitle
                title={content.title}
                onTitleUpdate={handleTitleUpdate}
              />
            </div>
            <div className={DashboardStyles.generatedHeaderIcons}>
              <div className={DashboardStyles.iconWrapper}>
                <img src={ShareSvg} alt="" />
              </div>
              <div className={DashboardStyles.iconWrapper}>
                <img src={SummarySvg} alt="" />
              </div>
              <div
                className={DashboardStyles.iconWrapper}
                onClick={handleThreeDotsClick}
                style={{ cursor: "pointer" }}
              >
                <img src={ThreeDotsSvg} alt="" />
              </div>
              <div className={DashboardStyles.iconWrapper}>
                <img src={ExpandSvg} alt="" />
              </div>
            </div>
          </div>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              sx: {
                borderRadius: "8px",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                minWidth: "150px",
              },
            }}
          >
            <MenuItem
              onClick={handleExportToWord}
              disabled={isExporting}
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#282B31",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-disabled": {
                  opacity: 0.6,
                },
              }}
            >
              <ListItemText
                primary={isExporting ? "Exporting..." : "Export (.DOCX)"}
              />
            </MenuItem>
          </Popover>

          <div className={DashboardStyles.generatedContent}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                cite: ({ node, ...props }) => {
                  const numbers = (props as any)["data-cite"] as string;
                  return (
                    <CitationTooltip
                      numbers={numbers}
                      citationsDict={content.citations_dict}
                      onClick={() => {
                        // Scroll to citations section
                        citationsRef.current?.scrollIntoView({
                          behavior: "smooth",
                        });
                      }}
                    />
                  );
                },
              }}
            >
              {replaceCitationsWithTags(markdownToShow)}
            </ReactMarkdown>
            {showCitations && (
              <>
                <br />
                <Divider />
                <br />
                <div ref={citationsRef}>
                  <Typography
                    className={DashboardStyles.citationsHeading}
                    component="h3"
                  >
                    Citations
                  </Typography>
                  <ol style={{ listStyle: "decimal" }}>
                    {content.citations_dict &&
                      Object.entries(content.citations_dict)
                        .sort(([a], [b]) => {
                          // Sort by number inside [n]
                          const numA = parseInt(a.replace(/\D/g, ""), 10);
                          const numB = parseInt(b.replace(/\D/g, ""), 10);
                          return numA - numB;
                        })
                        .map(([citationNumber, citationName], idx) => {
                          const url = content.citations?.[citationName];
                          return (
                            <li
                              key={citationNumber}
                              style={{ marginBottom: 6 }}
                            >
                              {url ? (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#3474E0",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {citationName}
                                </a>
                              ) : (
                                citationName
                              )}
                            </li>
                          );
                        })}
                  </ol>
                </div>
              </>
            )}
          </div>

          <div className={DashboardStyles.generatedContentFooter}>
            <div className={DashboardStyles.sourcesCitations}>
              <div
                className={DashboardStyles.sources}
                onClick={handleSourcesClick}
                style={{ cursor: "pointer" }}
                title="View sources details"
              >
                <Typography color="#282B31" fontWeight={500} fontSize={12}>
                  Sources {activeSources}/{totalSources}
                </Typography>
              </div>
              <div
                className={DashboardStyles.citations}
                style={{ cursor: "pointer" }}
                onClick={() => setShowCitations((prev) => !prev)}
                title={showCitations ? "Hide Citations" : "Show Citations"}
              >
                <img src={showCitations ? OpenEyeSvg : HiddenEyeSvg} alt="" />
                <Typography color="#282B31" fontWeight={400} fontSize={12}>
                  Citations
                </Typography>
              </div>
            </div>
            <div className={DashboardStyles.buttons}>
              <Button
                variant="outlined"
                style={{
                  borderRadius: "24px",
                  fontWeight: "500",
                  fontSize: "12px",
                  padding: "6px 16px",
                }}
                loading={savingDraft}
                onClick={handleSaveDraft}
              >
                Save Draft
              </Button>
              <Button
                variant="contained"
                style={{
                  borderRadius: "24px",
                  fontWeight: "500",
                  fontSize: "12px",
                  padding: "6px 16px",
                }}
                loading={publishing}
                onClick={handlePublish}
              >
                Publish
              </Button>
            </div>
          </div>

          {/* Sources Popover */}
          <Popover
            open={sourcesOpen}
            anchorEl={sourcesAnchorEl}
            onClose={handleSourcesPopoverClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                borderRadius: "12px",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                minWidth: "200px",
                maxWidth: "300px",
                border: "1px solid #E5E7EB",
              },
            }}
          >
            <Box sx={{ p: 2 }}>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Typography fontWeight={500} fontSize={14} color="#282B31">
                  Sources ({activeSources}/{totalSources})
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleSourcesPopoverClose}
                  sx={{
                    color: "#6B7280",
                    "&:hover": { backgroundColor: "#F3F4F6" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Sources List */}
              <List sx={{ p: 0 }}>
                {Object.entries(sourcesData).map(([sourceName, isActive]) => (
                  <ListItem
                    key={sourceName}
                    sx={{
                      px: 0,
                      py: 0.8,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {isActive ? (
                      <CheckCircleIcon
                        sx={{
                          color: "#10B981",
                          fontSize: "16px",
                          flexShrink: 0,
                          width: "8px",
                          height: "8px",
                        }}
                      />
                    ) : (
                      <img
                        src={GreyCrossSvg}
                        alt=""
                        style={{ flexShrink: "0" }}
                      />
                    )}
                    <Typography
                      fontWeight={400}
                      fontSize={10}
                      sx={{
                        color: isActive ? "#282B31" : "#7A7F89",
                        wordBreak: "break-word",
                      }}
                    >
                      {sourceName}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Popover>
        </div>
      </CardContent>
    </Card>
  );
};

export default GeneratedContent;
